//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import isEmpty from 'lodash/isEmpty'
import { stringifyQuery } from 'utils/strings'

import {
  useRefetchTicketMutation,
  useMoveChildrenMutation,
} from 'api/tickets'

import {
  useCreateTicketSubscriptionMutation,
  useRemoveTicketSubscriptionMutation,
} from 'api/subscriptions'

import {
  useDeleteTicketProviderTicketsMutation,
} from 'api/ticketProviderTickets'

import * as sessionUserSelector from 'selectors/sessionUser'
import {
  RouteTicketingSystemsTickets,
  RouteTicketingSystemsTicketEdit,
  RouteTicketingSystemsTicketClone,
  RouteTicketingSystemsTicketNew,
  RouteAppRequests,
  RouteTicketingSystemsTicketAppRequests,
  RouteAdminWebhookEvents,
} from 'routes'

import { useModalFormOperation } from 'components/Shared/ModalForm/hooks'
import { confirm } from 'components/Shared/ConfirmDialog/Index'
import ActionButtons from 'components/Shared/Skeleton/ActionButtons/Index'
import SubscriptionLink from './SubscriptionLink'
import EditModeLink from './EditModeLink'
import CloneLink from './CloneLink'
import CreateChildTicketLink from './CreateChildTicketLink'
import RefetchLink from './RefetchLink'
import RequestLink from './RequestLink'
import DeleteTicketLink from './DeleteTicketLink'
import WebhookLink from './WebhookLink'
import AddChildTicketsLink from './AddChildTicketsLink'
import MoveLink from './MoveLink'
import AddTicketsForm from './AddTicketsForm'
import MoveTicketForm from './MoveTicketForm'

const ButtonsToolbar = ({ ticket }) => {
  const { t } = useTranslation()
  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)
  const userIsPower = useSelector(sessionUserSelector.isPowerUser)
  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const userHasValidEmail = useSelector(sessionUserSelector.isValidEmail)
  const navigate = useNavigate()

  const [deleteTicketProviderTickets] = useDeleteTicketProviderTicketsMutation()
  const [refetchTicket] = useRefetchTicketMutation()
  const [createSubscription, { isLoading: isCreatingSubscription }] = useCreateTicketSubscriptionMutation()
  const [removeSubscription, { isLoading: isRemovingSubscription }] = useRemoveTicketSubscriptionMutation()
  const [moveChildren] = useMoveChildrenMutation()

  const moveChildrenModal = useModalFormOperation(moveChildren)

  const [isAddTicketsFormVisible, setAddTicketsFormVisible] = useState(false)
  const [isMoveTicketFormVisible, setMoveTicketFormVisible] = useState(false)

  const showAddTicketsForm = () => {
    setAddTicketsFormVisible(true)
  }

  const hideAddTicketsForm = () => {
    setAddTicketsFormVisible(false)
  }

  const onAddTicketsSubmit = (values) => {
    const childTicketIds = values.ticketList.map(v => v.shortLabel)

    moveChildrenModal({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      parentTicketId: ticket.externalId,
      childTicketIds: childTicketIds,
    })
  }

  const onEditClick = () => {
    navigate(RouteTicketingSystemsTicketEdit({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
    }))
  }

  const onCloneClick = () => {
    navigate(RouteTicketingSystemsTicketClone({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
    }))
  }

  const onCreateChildClick = () => {
    navigate(RouteTicketingSystemsTicketNew({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
    }))
  }

  const onRefetchClick = () => {
    refetchTicket({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
    })
  }

  const doDeleteTicket = () => {
    return deleteTicketProviderTickets({
      ticketProviderId: ticket.ticketProviderId,
      ticketIds: [ticket.providerTicketId],
    })
  }

  const onDeleteClick = () => {
    const title = t('ticketPage.removeTicketTitle')
    const message = t('ticketPage.removeTicketMessage',
      {
        number: ticket.externalId,
      }
    )

    confirm({
      title: title,
      message: message,
    }).then(
      () => {
        doDeleteTicket().then(fulfilled => {
          if (!fulfilled.data?.error) {
            navigate(RouteTicketingSystemsTickets({ ticketingSystemDisplayId: ticket.ticketingSystemDisplayId }))
          }
        })
      },
      () => {}
    )
  }

  const onSubscribeToggle = () => {
    const toggleSubscription = ticket.subscribed ? removeSubscription : createSubscription

    toggleSubscription({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
    })
  }

  const showMoveTicketForm = () => {
    setMoveTicketFormVisible(true)
  }

  const hideMoveTicketForm = () => {
    setMoveTicketFormVisible(false)
  }

  const onMoveTicketSubmit = (values) => {
    const parentNumber = values.parentTicket.shortLabel
    moveChildrenModal({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      parentTicketId: parentNumber,
      childTicketIds: [ticket.externalId],
    })
  }

  if (isEmpty(ticket)) {
    return null
  }

  const partnerAppRequestsLink = {
    pathname: RouteTicketingSystemsTicketAppRequests({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
    }),
  }

  const adminAppRequestsLink = {
    pathname: `${RouteAppRequests()}?${stringifyQuery({ filters: { ticket_id: ticket.externalId }, page: 1 })}`,
  }

  const adminWebhookEventsLink = {
    pathname: `${RouteAdminWebhookEvents()}?${stringifyQuery({ filters: { ticket_number: ticket.externalId }, page: 1 })}`,
  }

  if (isUserPrivileged) {
    const showRefetchLink = ticket.canBeReloaded && !userIsPower
    const showWebhookLink =
      Boolean(process.env.ENABLE_TS_WEBHOOKS === 'true') && ticket.enableWebhookEvents && !userIsPower

    return (
      <ActionButtons>
        {showRefetchLink && <RefetchLink onClick={onRefetchClick} />}
        {userIsAdmin && <DeleteTicketLink onClick={onDeleteClick} />}
        <RequestLink to={adminAppRequestsLink} />
        {showWebhookLink && <WebhookLink to={adminWebhookEventsLink} />}
      </ActionButtons>
    )
  } else {
    const showRequestLink = ticket.ticketingSystemHasApiAccess
    const isUpdatingSubscription = isCreatingSubscription || isRemovingSubscription
    return (
      <ActionButtons>
        <AddTicketsForm
          visible={isAddTicketsFormVisible}
          onSubmit={onAddTicketsSubmit}
          onClose={hideAddTicketsForm}
          ticket={ticket}
        />
        <MoveTicketForm
          visible={isMoveTicketFormVisible}
          onSubmit={onMoveTicketSubmit}
          onClose={hideMoveTicketForm}
          ticket={ticket}
        />
        <SubscriptionLink
          updatingSubscription={isUpdatingSubscription}
          subscribed={ticket.subscribed}
          onClick={onSubscribeToggle}
          isEmailValid={userHasValidEmail}
        />
        {ticket.canBeEdited && <EditModeLink onClick={onEditClick} />}
        {ticket.canBeCloned && <CloneLink onClick={onCloneClick} />}
        {ticket.canBeParent && <CreateChildTicketLink onClick={onCreateChildClick} />}
        {ticket.canMoveChildren && <AddChildTicketsLink onClick={showAddTicketsForm} />}
        {ticket.canChangeParent && <MoveLink onClick={showMoveTicketForm} />}
        {showRequestLink && <RequestLink to={partnerAppRequestsLink} />}
      </ActionButtons>
    )
  }
}

ButtonsToolbar.propTypes = {
  ticket: PropTypes.object,
}

export default ButtonsToolbar
